/**
 * Name parsing utilities that mirror the database trigger logic
 * for consistent name handling across the application
 */

export interface ParsedName {
  firstName: string | null;
  lastName: string | null;
}

/**
 * Parse a full name into first and last name using the same logic
 * as the database trigger `create_user_profile()`
 *
 * Logic mirrors the SQL:
 * - first_name := split_part(user_name, ' ', 1)
 * - last_name := substring(user_name FROM length(first_name) + 2)
 * - Handle single names by setting last_name to NULL
 *
 * @param fullName - The full name string to parse
 * @returns ParsedName object with firstName and lastName
 */
export function parseFullName(fullName: string | null | undefined): ParsedName {
  // Handle null, undefined, or empty string
  if (!fullName || fullName.trim() === '') {
    return {
      firstName: null,
      lastName: null,
    };
  }

  const trimmedName = fullName.trim();

  // Split by first space to get first name
  const firstSpaceIndex = trimmedName.indexOf(' ');

  if (firstSpaceIndex === -1) {
    // Single word name - treat as first name, last name is null
    return {
      firstName: trimmedName,
      lastName: null,
    };
  }

  // Extract first name (everything before first space)
  const firstName = trimmedName.substring(0, firstSpaceIndex);

  // Extract last name (everything after first space, trimmed)
  const lastName = trimmedName.substring(firstSpaceIndex + 1).trim();

  // If last name is empty after trimming, set to null
  return {
    firstName: firstName || null,
    lastName: lastName || null,
  };
}

/**
 * Check if a profile needs OAuth name synchronization
 * Returns true if EITHER first_name OR last_name are missing
 * If any name field is missing, we update BOTH fields from OAuth data
 * If both name fields are already set, we do NOT update anything
 *
 * @param profile - The user profile to check
 * @returns true if profile needs name sync, false otherwise
 */
export function needsOAuthNameSync(profile: {
  first_name?: string | null;
  last_name?: string | null;
}): boolean {
  return isFieldEmpty(profile.first_name) || isFieldEmpty(profile.last_name);
}

/**
 * Check if a field is considered empty (null, undefined, or empty string)
 *
 * @param field - The field value to check
 * @returns boolean - true if field is empty, false otherwise
 */
function isFieldEmpty(field: string | null | undefined): boolean {
  return field === null || field === undefined || field === '' || field.trim() === '';
}

/**
 * Check if OAuth user metadata contains name information
 *
 * @param userMetadata - The user metadata from Supabase OAuth
 * @returns true if name data is available, false otherwise
 */
export function hasOAuthNameData(userMetadata: any): boolean {
  return !!(
    userMetadata &&
    typeof userMetadata['name'] === 'string' &&
    userMetadata['name'].trim() !== ''
  );
}
